/**
 * useTableSort — IDI-479 shared click-to-sort hook for the shared Table.
 *
 *   const { sortedRows, getSortProps } = useTableSort(rows, columns, { onSortChange });
 *   <TableHeader {...getSortProps('amount')} align="right">Amount</TableHeader>
 *
 * `rows` is the page's already-filtered array in its default order; the
 * "off" state returns exactly that order. `columns` is an array of
 * `{ key, type, value }` (see utils/tableSort.js). Sorting is client-side
 * over the loaded rows and is memoised on `rows`, so progressively loaded
 * batches are re-sorted as they arrive.
 *
 * `onSortChange` fires after a header click (used by paginated pages to
 * reset to page 1). Pages may memoise `columns` to avoid re-sorting on
 * unrelated renders.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { sortRows, nextSortState } from '../../utils/tableSort';

export default function useTableSort(rows, columns, options = {}) {
  const [state, setState] = useState({ sortKey: null, sortDir: null });
  const onSortChangeRef = useRef(options.onSortChange);
  onSortChangeRef.current = options.onSortChange;

  const { sortKey, sortDir } = state;

  const sortedRows = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    if (!sortKey || !sortDir) return list;
    const column = (columns || []).find((c) => c.key === sortKey);
    if (!column) return list;
    return sortRows(list, column, sortDir);
    // `columns` may be re-created each render; that only costs a re-sort of
    // the loaded rows and keeps accessors that close over page state fresh.
  }, [rows, columns, sortKey, sortDir]);

  const toggleSort = useCallback((key) => {
    setState((prev) => nextSortState(prev, key));
    if (typeof onSortChangeRef.current === 'function') onSortChangeRef.current();
  }, []);

  const getSortProps = useCallback(
    (key) => ({
      sortKey: key,
      sortDirection: sortKey === key ? sortDir : null,
      onSort: toggleSort,
    }),
    [sortKey, sortDir, toggleSort]
  );

  return { sortedRows, sortKey, sortDir, toggleSort, getSortProps };
}

export { useTableSort };
